import {useQuery} from '@tanstack/react-query';
import {Fingerprint, Search} from 'lucide-react';
import {useDeferredValue, useState} from 'react';
import {apiRequest} from '../api';
import {Badge, Surface} from '../design';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatDateTime} from '../components/PagePrimitives';
import type {PageResult} from '../types';

type AuditHistoryEntry = {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  correlationId: string;
  createdAt: string;
  actorLabel: string;
  summary: string;
};

const readableAction = (action: string): string => action
  .replace(/[._]/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const AuditHistoryPage = () => {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search.trim());
  const query = deferredSearch.length >= 2 ? `&q=${encodeURIComponent(deferredSearch)}` : '';
  const history = useQuery({
    queryKey: ['audit-events', 'all', deferredSearch.length >= 2 ? deferredSearch : ''],
    queryFn: () => apiRequest<PageResult<AuditHistoryEntry>>(`/audit-events?limit=200${query}`),
  });

  return (
    <>
      <PageHeader
        eyebrow="Candidate-visible record"
        title="Every consequential change leaves a trail."
        description="Search the decisions, approvals, provider changes, and workflow transitions in your private workspace. Correlation IDs connect related operations without exposing provider payloads."
      />

      <Surface className="pd-history-toolbar">
        <label className="pd-search-control">
          <Search size={18} aria-hidden="true" />
          <span className="pd-visually-hidden">Search audit history</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search actions, record types, IDs, or correlation IDs"
            autoComplete="off"
          />
        </label>
        <span aria-live="polite">
          {history.data ? `${history.data.items.length} matching events` : 'Loading events'}
        </span>
      </Surface>

      {search.trim().length === 1 ? (
        <p className="pd-history-hint">Enter at least two characters to filter the timeline.</p>
      ) : null}
      {history.isLoading ? <LoadingPanel label="Loading audit history" /> : null}
      {history.isError ? <ErrorPanel error={history.error} retry={() => void history.refetch()} /> : null}
      {history.data?.items.length === 0 ? (
        <EmptyPanel
          title={deferredSearch.length >= 2 ? 'No history matches that search.' : 'No history has been recorded yet.'}
          description={deferredSearch.length >= 2
            ? 'Try an action such as approved, calendar, application, or a record ID.'
            : 'Events appear here as you build your profile and manage opportunities.'}
        />
      ) : null}

      {history.data?.items.length ? (
        <ol className="pd-audit-timeline pd-history-timeline">
          {history.data.items.map((entry) => (
            <li key={entry.id}>
              <span aria-hidden="true" />
              <Surface>
                <div className="pd-card-topline">
                  <strong>{readableAction(entry.action)}</strong>
                  <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
                </div>
                <div className="pd-history-entity">
                  <Badge>{entry.entityType.replaceAll('_', ' ')}</Badge>
                  <code>{entry.entityId}</code>
                </div>
                <p>{entry.summary}</p>
                <small>{entry.actorLabel} · correlation <code>{entry.correlationId}</code></small>
              </Surface>
            </li>
          ))}
        </ol>
      ) : null}

      <Surface className="pd-history-privacy-note">
        <Fingerprint size={20} aria-hidden="true" />
        <p>This timeline stores references and outcome metadata. It does not copy Gmail bodies, document contents, or provider error responses into audit events.</p>
      </Surface>
    </>
  );
};
